import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getLocale, formatDateLong } from '../../../helpers/dates';
import { fetchShow } from '../../../actions/appActions';
import { getCookie, setCookie } from '../../../helpers/cookie';
import { useTranslation } from 'react-i18next';
import styles from './statusCommissions.module.css';
import { getCategory } from '../../../helpers/category';
import { WelletContext } from '../../../context/WelletContext';
import useGenericTranslation from '../../../helpers/translate';
import ModalPickerSelector from '../../ModalSelector/ModalPickerSelector';
import { Spinner } from '../../Common/Spinner/Spinner';

const StatusCommissions = ({ categoryName, lastUpdate }) => {
  const { getClientTranslation } = useGenericTranslation();
  const { t } = useTranslation();
  const app = useSelector((state) => state.app);
  const locale = getLocale(app.language);
  const [restaurants, setRestaurants] = useState(null);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [showLevels, setShowLevels] = useState(false);
  const [showAllLevels, setShowAllLevels] = useState(false);
  const qualifyDateFormatted = formatDateLong(lastUpdate, locale);
  const welletContext = useContext(WelletContext);
  const restaurantCookie = getCookie('selectedRestaurant');
  const onRestaurantClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setCookie('selectedRestaurant', JSON.stringify(restaurant), 1440);
    getCommissions(restaurant.id);
  };
  const getRestaurants = async (selectedCity, page = 1, pageSize = 100) => {
    setLoadingRestaurants(true);
    try {
      const result = await fetchShow(app.language, selectedCity, page, pageSize, welletContext);

      const simplifiedRestaurants = result.shows.map((restaurant) => ({
        value: restaurant.id,
        label: restaurant.name,
      }));

      setRestaurants(simplifiedRestaurants);
      setSelectedRestaurant(
        restaurantCookie !== '' ? JSON.parse(restaurantCookie) : simplifiedRestaurants[0],
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const getCommissions = async (id = selectedRestaurant?.value) => {
    setLoading(true);
    try {
      const response = await welletContext.apis.tickets.get(`associates/getAllsalesCategory/${id}`);
      if (response.status === 200) {
        setCommissions(response.data.categoriesList.reverse());
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!restaurants) {
      getRestaurants();
    }
  }, []);

  useEffect(() => {
    if (restaurants) {
      getCommissions(selectedRestaurant.value);
      setShowLevels(true);
    }
  }, [restaurants]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className='font-smaller text-medium-gray'>
          {t('updatedOn')} {qualifyDateFormatted}
        </div>
      </div>

      <div className={styles.restaurantSection}>
        <div className={`${styles.restaurantLabel} mb-1 fw-semibold font-smaller`}>
          {getClientTranslation('title.plural')}
        </div>
        {loadingRestaurants ? (
          <div className='d-flex justify-content-center mt-3'>
            <Spinner size={30} color='#fff' />
          </div>
        ) : (
          <ModalPickerSelector
            title={t('campaign.selectRestaurant')}
            options={restaurants}
            selectedValue={selectedRestaurant}
            onChange={onRestaurantClick}
            placeholder={t('campaign.selectRestaurant')}
          />
        )}
      </div>

      {showLevels && !loading && commissions && (
        <div className={styles.levelsSection}>
          <div
            className={`${styles.levelsSummary} mb-3`}
            onClick={() => setShowAllLevels(!showAllLevels)}
          >
            <div className={styles.levelsSummaryContent}>
              <div className='text-medium fw-semibold'>
                {t('commissionLevels')} - {selectedRestaurant?.name}
              </div>
              <div className={`${styles.levelsSummaryBadge} font-extra-small fw-semibold `}>
                {commissions.length} {t('levels')}
              </div>
            </div>
            <div className={`${styles.expandIcon} ${showAllLevels ? styles.expandIconOpen : ''}`}>
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='var(--color-primary)'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <polyline points='6 9 12 15 18 9'></polyline>
              </svg>
            </div>
          </div>

          {showAllLevels && (
            <div className={`${styles.levelsList} mb-3`}>
              {commissions.map((commission, index) => {
                const isCurrent = categoryName === commission.name;
                return (
                  <div
                    key={index}
                    className={`${styles.levelCard} ${isCurrent ? styles.levelCardCurrent : ''}`}
                  >
                    <div className={styles.levelCardContent}>
                      <div className={styles.levelCardLeft}>
                        <div className=' font-tiny fw-semibold'>
                          {getCategory(commission.name, t)}
                        </div>
                        {isCurrent && (
                          <div className={`${styles.currentBadge} bold  font-extra-small `}>
                            {t('current')}
                          </div>
                        )}
                      </div>
                      <div className={`${styles.levelCardCommission} bold  font-normal`}>
                        {commission.commissionPercentage}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className={`${styles.disclaimer} font-extra-small `}>
            (*) {t('commissionDisclaimer')}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusCommissions;
